import { Fragment, ReactNode } from "react";

import { Menu, Transition } from "@headlessui/react";
import { ArrowPathIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";

export interface ActionMenuItem {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** When true the item is left out entirely (e.g. behind a feature flag). */
  hidden?: boolean;
  /** Renders the item in a destructive (red) style. */
  danger?: boolean;
}

interface ActionMenuProps {
  label: string;
  items: ActionMenuItem[];
  /** Trigger colour. "default" matches a standard ButtonInput, "primary" is green. */
  variant?: "default" | "primary";
  /** Which edge the menu aligns to. Defaults to right. */
  align?: "left" | "right";
  disabled?: boolean;
  /** Extra classes for the trigger button. */
  classes?: string;
}

const variantClasses: Record<NonNullable<ActionMenuProps["variant"]>, string> = {
  default: "bg-primary-600 hover:bg-primary-700",
  primary: "bg-green-600 hover:bg-green-700",
};

/**
 * A dropdown menu of actions styled to sit alongside ButtonInput in toolbars.
 * Items flagged `hidden` are dropped; if nothing is left the trigger renders
 * nothing so callers don't need to guard on an empty menu.
 */
export default function ActionMenu({
  label,
  items,
  variant = "default",
  align = "right",
  disabled = false,
  classes,
}: ActionMenuProps) {
  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        disabled={disabled}
        className={classNames(
          "inline-flex items-center gap-1 rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-500 disabled:hover:bg-gray-500",
          variantClasses[variant],
          classes
        )}
      >
        {label}
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={classNames(
            "absolute z-50 mt-1 w-56 origin-top rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
            align === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left"
          )}
        >
          <div className="py-1">
            {visibleItems.map((item, index) => (
              <Menu.Item key={index} disabled={item.disabled}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={item.onClick}
                    disabled={item.disabled}
                    className={classNames(
                      "block w-full px-4 py-2 text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed",
                      item.danger
                        ? active
                          ? "bg-red-50 text-red-700"
                          : "text-red-600"
                        : active
                          ? "bg-slate-50 text-slate-900"
                          : "text-slate-700"
                    )}
                  >
                    {item.label}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
